import React, { useEffect, useState } from 'react';
import { Cookie, Shield, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import useConsent from '../hooks/useConsent';
import { Button } from './ui/button';

/**
 * CookieConsent — CNIL/RGPD-compliant banner + preferences modal.
 *
 * Emits a global custom event 'open-cookie-preferences' that can be dispatched
 * from anywhere (e.g. Footer "Gérer mes cookies" button) to reopen the modal.
 */
const CookieConsent = () => {
  const { consent, hasChosen, acceptAll, rejectAll, savePreferences } = useConsent();
  const [modalOpen, setModalOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  // Listen for global "open preferences" events (from Footer button)
  useEffect(() => {
    const handler = () => {
      setPrefs({
        analytics: consent?.analytics || false,
        marketing: consent?.marketing || false,
      });
      setModalOpen(true);
    };
    window.addEventListener('open-cookie-preferences', handler);
    return () => window.removeEventListener('open-cookie-preferences', handler);
  }, [consent]);

  const handleSave = () => {
    savePreferences(prefs);
    setModalOpen(false);
  };

  // Never show banner if user has already chosen (unless preferences modal reopened via event)
  const showBanner = !hasChosen && !modalOpen;

  return (
    <>
      {/* Banner */}
      {showBanner && (
        <div
          data-testid="cookie-banner"
          className="fixed bottom-0 left-0 right-0 z-[9998] bg-[#1a1c1b] border-t-2 border-[#d9fb06] shadow-2xl animate-slide-up"
          role="dialog"
          aria-labelledby="cookie-banner-title"
        >
          <div className="max-w-6xl mx-auto p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-shrink-0 flex items-center gap-3">
              <div className="w-11 h-11 bg-[#d9fb06]/10 rounded-full flex items-center justify-center">
                <Cookie className="text-[#d9fb06]" size={22} />
              </div>
            </div>
            <div className="flex-1">
              <h2 id="cookie-banner-title" className="font-black text-[#d9fb06] uppercase text-base mb-1">
                Vos cookies, votre choix
              </h2>
              <p className="text-[#dfddd6] text-sm leading-relaxed">
                Nous utilisons des cookies pour mesurer l'audience de notre site (PostHog).
                Vos données ne sont jamais revendues. Vous pouvez accepter, refuser ou personnaliser vos choix.
                {' '}<Link to="/politique-cookies" className="text-[#d9fb06] underline hover:no-underline">
                  En savoir plus
                </Link>.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <Button
                onClick={() => { setPrefs({ analytics: false, marketing: false }); setModalOpen(true); }}
                data-testid="cookie-personalize-btn"
                className="rounded-full text-xs font-bold uppercase px-4 py-2 bg-transparent border border-[#888680] text-[#dfddd6] hover:border-[#d9fb06] hover:text-[#d9fb06]"
              >
                <Settings size={14} className="mr-1.5" />
                Personnaliser
              </Button>
              <Button
                onClick={rejectAll}
                data-testid="cookie-reject-all-btn"
                className="rounded-full text-xs font-bold uppercase px-4 py-2 bg-transparent border border-[#888680] text-[#dfddd6] hover:border-[#d9fb06] hover:text-[#d9fb06]"
              >
                Tout refuser
              </Button>
              <Button
                onClick={acceptAll}
                data-testid="cookie-accept-all-btn"
                className="rounded-full text-xs font-bold uppercase px-4 py-2 bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90"
              >
                Tout accepter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {modalOpen && (
        <div
          data-testid="cookie-preferences-modal"
          className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4 animate-fade-in"
          onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}
        >
          <div className="bg-[#1a1c1b] border-2 border-[#d9fb06] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 md:p-6 border-b border-[#3f4816] flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#d9fb06]/10 rounded-full flex items-center justify-center">
                  <Shield className="text-[#d9fb06]" size={20} />
                </div>
                <div>
                  <h2 className="font-black text-[#d9fb06] uppercase text-lg">Préférences cookies</h2>
                  <p className="text-xs text-[#888680] mt-0.5">Contrôlez précisément vos données</p>
                </div>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-[#888680] hover:text-[#d9fb06] transition-colors"
                aria-label="Fermer"
                data-testid="cookie-preferences-close-btn"
              >
                <X size={22} />
              </button>
            </div>

            <div className="p-5 md:p-6 space-y-4">
              {/* Essential */}
              <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-[#dfddd6] mb-1">Cookies essentiels</h3>
                    <p className="text-xs text-[#888680] leading-relaxed">
                      Indispensables au fonctionnement du site : session admin, préférences d'affichage, formulaires.
                      Ces cookies ne peuvent pas être désactivés.
                    </p>
                  </div>
                  <div className="flex-shrink-0 flex items-center">
                    <span className="text-xs font-bold uppercase text-[#d9fb06]" data-testid="cookie-toggle-essential-status">Toujours actif</span>
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-[#dfddd6] mb-1">Cookies analytiques</h3>
                    <p className="text-xs text-[#888680] leading-relaxed">
                      Mesurent votre navigation pour améliorer le site (PostHog, hébergé aux États-Unis avec DPA signé).
                      Anonymisés autant que possible. Durée : 12 mois.
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                    testId="cookie-toggle-analytics"
                    label="Analytiques"
                  />
                </div>
              </div>

              {/* Marketing */}
              <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-[#dfddd6] mb-1">Cookies marketing</h3>
                    <p className="text-xs text-[#888680] leading-relaxed">
                      Actuellement <strong>non utilisés</strong>. Réservés pour de futures campagnes publicitaires ciblées.
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={prefs.marketing}
                    onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                    testId="cookie-toggle-marketing"
                    label="Marketing"
                  />
                </div>
              </div>

              <p className="text-xs text-[#888680] leading-relaxed pt-2">
                Vous pouvez modifier ces préférences à tout moment via le lien "Gérer mes cookies" en bas de page.
                Le consentement est enregistré pendant 6 mois (recommandation CNIL).
              </p>
            </div>

            <div className="p-5 md:p-6 border-t border-[#3f4816] flex flex-wrap gap-2 justify-end">
              <Button
                onClick={() => { rejectAll(); setModalOpen(false); }}
                data-testid="cookie-modal-reject-btn"
                className="rounded-full text-xs font-bold uppercase px-4 py-2 bg-transparent border border-[#888680] text-[#dfddd6] hover:border-[#d9fb06] hover:text-[#d9fb06]"
              >
                Tout refuser
              </Button>
              <Button
                onClick={handleSave}
                data-testid="cookie-modal-save-btn"
                className="rounded-full text-xs font-bold uppercase px-6 py-2 bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90"
              >
                Enregistrer mes choix
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ToggleSwitch = ({ checked, onChange, testId, label }) => (
  <button
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#d9fb06] focus:ring-offset-2 focus:ring-offset-[#302f2c] ${
      checked ? 'bg-[#d9fb06]' : 'bg-[#3f4816]'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 rounded-full bg-[#1a1c1b] transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

export default CookieConsent;
